/**
 * Payment abstraction — types only. No money moves in this code base yet.
 *
 * The browser talks to a PaymentProvider; the provider talks to nobody
 * (disabled mode) or, in the future, to the Worker, which talks to PAYUNi.
 * Nothing here knows a PAYUNi field, a merchant id or a transaction id.
 *
 *   PAYMENT_MODE  disabled   beginPayment / verifyPayment succeed at once, clearly
 *                            marked as a bypass; a local dev_… order id, no money.
 *                 test       PAYUNi sandbox through the Worker (not implemented).
 *                 live       PAYUNi production through the Worker (not implemented).
 *
 * In test / live mode the browser never declares a payment confirmed: the
 * Worker verifies the PAYUNi callback and issues the TranslationEntitlement.
 */

export const PAYMENT_MODES = ['disabled', 'test', 'live'] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];

export function isPaymentMode(value: unknown): value is PaymentMode {
  return typeof value === 'string' && (PAYMENT_MODES as readonly string[]).includes(value);
}

/** Strict: an unknown value throws, so a typo can never silently bypass payment. */
export function parsePaymentMode(value: unknown): PaymentMode {
  if (isPaymentMode(value)) return value;
  throw new Error(`PAYMENT_MODE must be one of ${PAYMENT_MODES.join(' | ')}, got ${JSON.stringify(value)}`);
}

export const PAYMENT_STATUSES = ['PENDING', 'CONFIRMED', 'FAILED', 'CANCELLED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** Who established the payment status. Only 'worker' may ever mean real money. */
export type PaymentAuthority = 'disabled-bypass' | 'worker';

/** What the browser asks a provider to collect: the confirmed quote's ids and amount, no PDF text. */
export interface PaymentRequest {
  quoteId: string;
  /** NT$ (integer, the quotedAmount of the confirmed quote). */
  amount: number;
  documentFingerprint: string;
  scopeFingerprint: string;
  /** ISO; the order is refused after this. */
  quoteExpiresAt: string;
}

export interface PaymentResult {
  paymentMode: PaymentMode;
  quoteId: string;
  /** disabled: a local temporary id (dev_…); never a PAYUNi transaction id. */
  orderId: string;
  amount: number;
  currency: 'TWD';
  status: PaymentStatus;
  /** ISO timestamps. */
  createdAt: string;
  confirmedAt: string | null;
  /** True when no money moved (disabled mode). Never true in live mode. */
  bypassed: boolean;
  authority: PaymentAuthority;
}

export const ENTITLEMENT_STATUSES = ['ACTIVE', 'USED', 'REVOKED', 'EXPIRED'] as const;
export type EntitlementStatus = (typeof ENTITLEMENT_STATUSES)[number];

/**
 * The right to run ONE paid translation of ONE scope of ONE document.
 * disabled: issued locally, temporary. live: issued by the Worker after it
 * verified the payment (signing is a later step; none here).
 */
export interface TranslationEntitlement {
  quoteId: string;
  orderId: string;
  documentFingerprint: string;
  scopeFingerprint: string;
  quotedAmount: number;
  status: EntitlementStatus;
  issuedAt: string;
  expiresAt: string;
  issuedBy: 'local-disabled' | 'worker';
}

/**
 * The one interface main.ts uses. Future PAYUNi code implements it in its own
 * module (web/src/payment/payuni.ts); nothing PAYUNi-specific enters main.ts.
 */
export interface PaymentProvider {
  readonly mode: PaymentMode;
  /**
   * Create (or return the existing) order for a confirmed quote. Idempotent per
   * quoteId: a second call never creates a second payment.
   */
  beginPayment(request: PaymentRequest): Promise<PaymentResult>;
  /** Current status of an order. Idempotent. The provider, never the caller, decides CONFIRMED. */
  verifyPayment(orderId: string): Promise<PaymentResult>;
  /** Entitlement for a CONFIRMED payment; throws for any other status. Idempotent per orderId. */
  issueEntitlement(payment: PaymentResult, request: PaymentRequest): Promise<TranslationEntitlement>;
}
